import {
  Box,
  Button,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  CircularProgress,
  Modal,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  GridToolbarQuickFilter,
  GridCsvExportMenuItem,
  GridPrintExportMenuItem,
} from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { tokens } from "../dashboard/theme";
import NavigationHeader from "../components/NavigationHeader";
import config from "./global/config";
import { usePermissions } from "./hooks/usePermissions";
import {
  AddFactureModal,
  // EditFactureModal,
  DeleteFactureModal,
} from "./FacturesModals";
import CashPaymentModal from "./CashPaymentModal";
import PaymentIcon from "@mui/icons-material/Payment";
import InstallmentPaymentModal from "./InstallmentPaymentModal";
import FactureDetailsGrid from "./FactureDetailsGrid";
import PaymentDetailsGrid from "./PaymentDetailsGrid";
import FacturesFilter from "./FacturesFilter";
import { format, startOfDay, endOfDay } from "date-fns";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import PrintFacture from "./PrintFacture";
import { Print } from "@mui/icons-material";

function FacturesManagement() {
  const { hasPermission } = usePermissions();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const [activeTab, setActiveTab] = useState(0);
  const [factures, setFactures] = useState([]);
  const [factureDetails, setFactureDetails] = useState([]);
  const [selectedFacture, setSelectedFacture] = useState(null);
  const [modalAdd, setModalAdd] = useState(false);
  // const [modalEdit, setModalEdit] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [installmentModalOpen, setInstallmentModalOpen] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const today = new Date();
  const [filters, setFilters] = useState({
    id_client: "",
    date_debut: startOfDay(today),
    date_fin: endOfDay(today),
    statut_paiement: "",
  });
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [selectedPrintFacture, setSelectedPrintFacture] = useState(null);

  const handlePrint = (facture) => {
    setSelectedPrintFacture(facture);
    setPrintModalOpen(true);
  };

  const api = axios.create({
    baseURL: config.apiUrl,
  });

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchFacturesWithFilters(newFilters);
  };

  const ensureArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return [data];
  };

  const fetchFacturesWithFilters = async (currentFilters = filters) => {
    try {
      let queryParams = new URLSearchParams();

      if (currentFilters.id_client) {
        queryParams.append("id_client", currentFilters.id_client);
      }
      if (currentFilters.date_debut) {
        queryParams.append(
          "date_debut",
          format(currentFilters.date_debut, "yyyy-MM-dd")
        );
      }
      if (currentFilters.date_fin) {
        queryParams.append(
          "date_fin",
          format(currentFilters.date_fin, "yyyy-MM-dd")
        );
      }
      if (currentFilters.statut_paiement) {
        queryParams.append("statut_paiement", currentFilters.statut_paiement);
      }

      const response = await api.get(`factures.php?${queryParams.toString()}`);
      const facturesData = ensureArray(response.data);
      setFactures(facturesData);
    } catch (error) {
      console.error("Error fetching factures:", error);
      setError(error.message);
      if (error.response?.status === 401) {
        window.location.href = "/login";
      }
    }
  };

  const fetchFactures = async () => {
    try {
      const response = await api.get("factures.php");
      const facturesData = ensureArray(response.data);
      setFactures(facturesData);
    } catch (error) {
      console.error("Error fetching factures:", error);
      if (error.response?.status === 401) {
        window.location.href = "/login";
      }
    }
  };

  const handleAddFactureDetail = async (detailData) => {
    console.log("handleAddFactureDetail: ", detailData);
    try {
      const formData = new FormData();
      formData.append("METHOD", "POST");
      formData.append("id_facture", detailData.id_facture);
      formData.append("id_produit", detailData.id_produit);
      formData.append("quantite", detailData.quantite);
      formData.append(
        "prix_facture_unitaire",
        detailData.prix_facture_unitaire
      );
      formData.append("user_cre", "SYSTEM");

      await api.post("details_factures.php", formData);
      fetchFactureDetails(selectedFacture.id_facture);
    } catch (error) {
      console.error("Error adding facture detail:", error);
    }
  };

  const handleEditFactureDetail = async (id_detail_facture, detailData) => {
    try {
      const formData = new FormData();
      formData.append("METHOD", "PUT");
      formData.append("id_produit", detailData.id_produit);
      formData.append("quantite", detailData.quantite);
      formData.append(
        "prix_facture_unitaire",
        detailData.prix_facture_unitaire
      );
      formData.append("user_upd", "SYSTEM");

      await api.post(
        `details_factures.php?id_detail_facture=${id_detail_facture}`,
        formData
      );
      fetchFactureDetails(selectedFacture.id_facture);
    } catch (error) {
      console.error("Error updating facture detail:", error);
    }
  };

  const handleDeleteFactureDetail = async (id_detail_facture) => {
    try {
      const formData = new FormData();
      formData.append("METHOD", "DELETE");
      formData.append("user_upd", localStorage.getItem("username") || "SYSTEM");

      const response = await api.post(
        `details_factures.php?id_detail_facture=${id_detail_facture}`,
        formData
      );

      if (response.data.success) {
        fetchFactureDetails(selectedFacture.id_facture);
      } else {
        throw new Error(response.data.error || "Failed to delete facture detail");
      }
    } catch (error) {
      console.error("Error deleting facture detail:", error);
      throw error;
    }
  };
  
  const handlePaymentComplete = async () => {
    try {
      await fetchFactures();

      if (selectedFacture) {
        const response = await api.get(
          `factures.php?id_facture=${selectedFacture.id_facture}`
        );
        const updatedFacture = response.data[0];
        setSelectedFacture(updatedFacture);

        // if (activeTab === 2) {
        //   await fetchPaymentDetails(selectedFacture.id_facture);
        // }
      }

      setPaymentModalOpen(false);
      setInstallmentModalOpen(false);
    } catch (error) {
      console.error("Error refreshing data after payment:", error);
    }
  };

  const validateNumericValue = (value, fallback = 0) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  };

  const handleAddFacture = async (factureData) => {
    try {
      if (
        !factureData.id_client ||
        !factureData.type_paiement ||
        !factureData.date_facture
      ) {
        throw new Error(
          "Missing required fields: Client, Payment Type, and Date are required"
        );
      }

      // Ensure montant_total is a valid number
      const validatedTotal = validateNumericValue(factureData.montant_total, 0);
      const formattedTotal = validatedTotal.toFixed(2);

      const factureFormData = new FormData();
      factureFormData.append("METHOD", "POST");
      factureFormData.append("id_client", factureData.id_client);
      factureFormData.append("categorie_code", "1");
      factureFormData.append("date_facture", factureData.date_facture);
      factureFormData.append("montant_total", formattedTotal);
      factureFormData.append("type_paiement", factureData.type_paiement);
      factureFormData.append(
        "statut_paiement",
        factureData.statut_paiement || "En attente"
      );
      factureFormData.append("commentaire", factureData.commentaire || "");
      factureFormData.append("user_cre", "SYSTEM");

      const factureResponse = await api.post("factures.php", factureFormData);

      if (
        !factureResponse.data ||
        typeof factureResponse.data.id_facture === "undefined"
      ) {
        console.error("Invalid facture response:", factureResponse.data);
        throw new Error(
          factureResponse.data?.error || "Invalid response format from server"
        );
      }

      const factureId = factureResponse.data.id_facture;

      // Handle facture details
      if (
        Array.isArray(factureData.details) &&
        factureData.details.length > 0
      ) {
        const detailPromises = factureData.details.map(async (detail) => {
          if (!detail.id_produit || !detail.quantite) {
            console.warn("Skipping invalid detail:", detail);
            return null;
          }

          const validatedPrice = validateNumericValue(
            detail.prix_facture_unitaire,
            0
          );
          const validatedQuantity = validateNumericValue(detail.quantite, 0);

          const detailFormData = new FormData();
          detailFormData.append("METHOD", "POST");
          detailFormData.append("id_facture", factureId);
          detailFormData.append("id_produit", detail.id_produit);
          detailFormData.append("quantite", validatedQuantity.toString());
          detailFormData.append(
            "prix_facture_unitaire",
            validatedPrice.toFixed(2)
          );
          detailFormData.append("user_cre", "SYSTEM");

          try {
            const detailResponse = await api.post(
              "details_factures.php",
              detailFormData
            );
            return detailResponse.data;
          } catch (error) {
            console.error("Error creating detail:", error);
            throw error;
          }
        });

        await Promise.all(detailPromises.filter(Boolean));
      }

      setModalAdd(false);
      await fetchFactures();

      return {
        success: true,
        message: "Facture created successfully",
        id: factureId,
      };
    } catch (error) {
      console.error("Error in facture creation:", error);
      setError(
        error.message ||
          "An unexpected error occurred while creating the facture"
      );

      return {
        success: false,
        message: error.message || "Failed to create facture",
      };
    }
  };

  const handleDeleteFacture = async (factureData) => {
    try {
      setError(null);
      const formData = new FormData();
      formData.append("METHOD", "DELETE");

      const response = await api.post(
        `factures.php?id_facture=${factureData.id_facture}`,
        formData
      );

      if (response.data.success) {
        setModalDelete(false);
        // Refresh the factures list
        await fetchFacturesWithFilters();
        return {
          success: true,
          message: "Facture deleted successfully",
        };
      } else {
        throw new Error(response.data.error || "Failed to delete facture");
      }
    } catch (error) {
      console.error("Error deleting facture:", error);
      setError(error.message || "An unexpected error occurred while deleting the facture");
      return {
        success: false,
        message: error.message || "Failed to delete facture",
      };
    }
  };

  
  const facturesColumns = [
    {
      field: "numero_facture",
      headerName: "N° Facture",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "client_nom",
      headerName: "Client",
      flex: 1,
    },
    {
      field: "date_facture",
      headerName: "Date",
      flex: 1,
    },
    {
      field: "montant_total",
      headerName: "Montant Total",
      flex: 1,
    },
    {
      field: "type_paiement",
      headerName: "Type Paiement",
      flex: 1,
    },
    {
      field: "statut_paiement",
      headerName: "Statut",
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 180,
      getActions: (params) => {
        const actions = [];
        actions.push(
          <GridActionsCellItem
            icon={<Print />}
            label="Print"
            onClick={() => handlePrint(params.row)}
            showInMenu={false}
          />
        );

        // View Details Action
        actions.push(
          <GridActionsCellItem
            icon={<VisibilityIcon />}
            label="View Details"
            onClick={() => handleViewDetails(params.row)}
            showInMenu={false}
          />
        );

        if (hasPermission("EDIT_SALE")) {
          actions.push(
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={() => handleEdit(params.row)}
              showInMenu={false}
            />
          );
        }

        if (hasPermission("DELETE_SALE")) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => handleDelete(params.row)}
              showInMenu={false}
            />
          );
        }

        return actions;
      },
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [clientsRes, productsRes] = await Promise.all([
        api.get("client.php"),
        api.get("produits.php"),
      ]);

      setClients(clientsRes.data || []);
      setProducts(productsRes.data || []);

      // Fetch factures with initial filter (today's date)
      await fetchFacturesWithFilters();
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
      if (error.response?.status === 401) {
        window.location.href = "/login";
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFactureDetails = async (id_facture) => {
    try {
      const response = await api.get(
        `details_factures.php?id_facture=${id_facture}`
      );
      if (response.data) {
        console.log("Facture details:", response.data);
        setFactureDetails(response.data);
        fetchData();
      }
    } catch (error) {
      console.error("Error fetching facture details:", error);
      setError(error.message);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get("produits.php");
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleViewDetails = (facture) => {
    setSelectedFacture(facture);
    fetchFactureDetails(facture.id_facture);
    setActiveTab(1);
  };

  const handleEdit = (facture) => {
    setSelectedFacture(facture);
    // setModalEdit(true);
  };

  const handleDelete = (facture) => {
    setSelectedFacture(facture);
    setModalDelete(true);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      setSelectedFacture(null);
      setFactureDetails([]);
    }
  };
  if (error) {
    return (
      <Box m="25px">
        <NavigationHeader title="Erreur" subtitle="Une erreur est survenue" />
        <Box sx={{ color: "error.main", mt: 2 }}>{error}</Box>
      </Box>
    );
  }

  return (
    <Box m="25px">
      {/* Modals */}
      {selectedPrintFacture && (
        <Modal
          open={printModalOpen}
          onClose={() => setPrintModalOpen(false)}
          aria-labelledby="print-modal"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "80%",
              maxHeight: "90vh",
              overflow: "auto",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
            }}
          >
            <PrintFacture
              factureData={selectedPrintFacture}
              onClose={() => setPrintModalOpen(false)}
            />
          </Box>
        </Modal>
      )}
      {selectedFacture && (
        <>
          <InstallmentPaymentModal
            open={installmentModalOpen}
            handleClose={() => setInstallmentModalOpen(false)}
            factureData={selectedFacture}
            onPaymentComplete={handlePaymentComplete}
          />
          <CashPaymentModal
            open={paymentModalOpen}
            handleClose={() => setPaymentModalOpen(false)}
            factureData={selectedFacture}
            onPaymentComplete={handlePaymentComplete}
          />

          {/* {modalEdit && (
              <EditFactureModal
                open={modalEdit}
                handleClose={() => setModalEdit(false)}
                handleSubmit={handleEditFacture}
                facture={selectedFacture}
                clients={clients}
                products={products}
              />
            )} */}

          {modalDelete && (
            <DeleteFactureModal
              open={modalDelete}
              handleClose={() => setModalDelete(false)}
              handleDelete={() => handleDeleteFacture(selectedFacture)}
              facture={selectedFacture}
            />
          )}
        </>
      )}

      <AddFactureModal
        open={modalAdd}
        handleClose={() => setModalAdd(false)}
        handleSubmit={handleAddFacture}
        clients={clients}
        products={products}
        onProductsUpdated={fetchProducts}
      />

      <NavigationHeader
        title="Gestion des Factures"
        subtitle={
          selectedFacture
            ? `Facture N° ${selectedFacture.numero_facture}`
            : "Liste des factures"
        }
      />

      {/* Filter Toggle and Factures Filter Component */}
      {activeTab === 0 && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowFilter(!showFilter)}
            startIcon={showFilter ? <FilterAltIcon /> : <FilterAltIcon />}
            sx={{
              mb: 1,
              color:
                theme.palette.mode === "light"
                  ? colors.primary[100]
                  : colors.greenAccent[500],
            }}
          >
            {showFilter ? "Masquer les filtres" : "Afficher les filtres"}
          </Button>
          {showFilter && (
            <FacturesFilter
              clients={clients}
              onFilterChange={handleFilterChange}
            />
          )}
        </Box>
      )}

      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tab
          label="Factures"
          style={
            activeTab === 0
              ? {
                  color:
                    theme.palette.mode === "light"
                      ? colors.primary[100]
                      : colors.greenAccent[500],
                }
              : {}
          }
        />
        <Tab
          label="Détails de Facture"
          disabled={!selectedFacture}
          style={
            activeTab === 1
              ? {
                  color:
                    theme.palette.mode === "light"
                      ? colors.primary[100]
                      : colors.greenAccent[500],
                }
              : {}
          }
        />
      </Tabs>

      <Box display="flex" justifyContent="end" mt="20px">
        {activeTab === 0 && hasPermission("CREATE_SALE") && (
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            onClick={() => setModalAdd(true)}
          >
            Nouvelle Facture
          </Button>
        )}
      </Box>

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.secondary,
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
          "& .Mui-disabled": {
            opacity: 0.38,
            pointerEvents: "none",
            cursor: "default",
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: `${colors.primary[900]} !important`,
          },
        }}
      >
        {error ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            color="error.main"
          >
            {error}
          </Box>
        ) : loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress />
          </Box>
        ) : // Update the DataGrid section in FacturesManagement.jsx
        activeTab === 0 ? (
          <DataGrid
            rows={factures}
            columns={facturesColumns}
            getRowId={(row) => row.id_facture}
            slots={{
              toolbar: CustomToolbar,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                pageSize: 25,
              },
              sorting: {
                sortModel: [{ field: "date_facture", sort: "desc" }],
              },
            }}
          />
        ) : (
          <FactureDetailsGrid
            factureDetails={factureDetails}
            products={products}
            loading={loading}
            colors={colors}
            factureId={selectedFacture?.id_facture}
            onAddDetail={handleAddFactureDetail}
            onEditDetail={handleEditFactureDetail}
            onDeleteDetail={handleDeleteFactureDetail}
          />
        )}
      </Box>
    </Box>
  );
}

const CustomToolbar = (props) => {
  return (
    <GridToolbarContainer {...props}>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <GridToolbarExportContainer>
        <GridCsvExportMenuItem options={{ utf8WithBom: true }} />
        <GridPrintExportMenuItem />
      </GridToolbarExportContainer>
      <Box sx={{ marginLeft: "auto" }}>
        <GridToolbarQuickFilter />
      </Box>
    </GridToolbarContainer>
  );
};

export default FacturesManagement;
